import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth-context';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import AppLayout from './components/AppLayout';
import RouteLoader from './components/RouteLoader';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Code-split every protected page so the initial bundle only ships
// what's needed to render the login / dashboard skeleton. Each lazy
// import becomes its own webpack chunk — fetched on first visit only.
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AssetsPage = lazy(() => import('./pages/AssetsPage'));
const InspectionPage = lazy(() => import('./pages/InspectionPage'));
const OrangeListPage = lazy(() => import('./pages/OrangeListPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const InspectionHistoryPage = lazy(() => import('./pages/InspectionHistoryPage'));
const SchedulesPage = lazy(() => import('./pages/SchedulesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const PerformanceSheetPage = lazy(() => import('./pages/PerformanceSheetPage'));
const InspectionCompliancePage = lazy(() => import('./pages/InspectionCompliancePage'));
const StationCanvasPage = lazy(() => import('./pages/StationCanvasPage'));
const QRScanLandingPage = lazy(() => import('./pages/QRScanLandingPage'));
const PublicComplaintPage = lazy(() => import('./pages/PublicComplaintPage'));
const QRScannerPage = lazy(() => import('./pages/QRScannerPage'));
const DefectSlasPage = lazy(() => import('./pages/DefectSlasPage'));
const AIInsightsPage = lazy(() => import('./pages/AIInsightsPage'));
const AIInsightsTelemetryPage = lazy(() => import('./pages/AIInsightsTelemetryPage'));
const PerformancePage = lazy(() => import('./pages/PerformancePage'));
const OngoingInspectionsPage = lazy(() => import('./pages/OngoingInspectionsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));

function ProtectedRoute({ children, adminOnly = false, blockViewer = false, allowedRoles = null }) {
  const { user, loading } = useAuth();

  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && !['superadmin', 'admin'].includes(user.role)) return <Navigate to="/" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" />;
  if (blockViewer && user.role === 'viewer') return <Navigate to="/" />;

  return children;
}

/** Login route: if already authenticated, honour ?redirect= param */
function LoginRoute() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  if (user) return <Navigate to={redirectTo || '/'} replace />;
  return <LoginPage />;
}

/** Wrap a route element in <Suspense> so each lazy chunk has a fallback. */
function Lazy({ children }) {
  return (
    <ErrorBoundary level="page">
      <Suspense fallback={<RouteLoader />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  const { loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout><Lazy><DashboardPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/assets" element={
        <ProtectedRoute>
          <AppLayout><Lazy><AssetsPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/inspection" element={
        <ProtectedRoute blockViewer>
          <AppLayout><Lazy><InspectionPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/inspection-history" element={
        <ProtectedRoute>
          <AppLayout><Lazy><InspectionHistoryPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/orange-list" element={
        <ProtectedRoute>
          <AppLayout><Lazy><OrangeListPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/schedules" element={
        <ProtectedRoute>
          <AppLayout><Lazy><SchedulesPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <AppLayout><Lazy><NotificationsPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['superadmin', 'admin', 'divisional_admin']}>
          <AppLayout><Lazy><AdminPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <AppLayout><Lazy><ProfilePage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <AppLayout><Lazy><ReportsPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/performance/:userId" element={
        <ProtectedRoute>
          <AppLayout><Lazy><PerformanceSheetPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/inspection-monitor" element={
        <ProtectedRoute>
          <AppLayout><Lazy><InspectionCompliancePage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/station-canvas" element={
        <ProtectedRoute>
          <AppLayout><Lazy><StationCanvasPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/ongoing-inspections" element={
        <ProtectedRoute>
          <AppLayout><Lazy><OngoingInspectionsPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      {/* Public routes — no auth required */}
      <Route path="/scan/:subzoneId" element={<Lazy><QRScanLandingPage /></Lazy>} />
      <Route path="/public-complaint/:subzoneId" element={<Lazy><PublicComplaintPage /></Lazy>} />
      <Route path="/privacy" element={<Lazy><PrivacyPage /></Lazy>} />
      <Route path="/qr-scanner" element={
        <ProtectedRoute>
          <AppLayout><Lazy><QRScannerPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/defect-slas" element={
        <ProtectedRoute>
          <AppLayout><Lazy><DefectSlasPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/ai-insights" element={
        <ProtectedRoute>
          <AppLayout><Lazy><AIInsightsPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/ai-insights/telemetry" element={
        <ProtectedRoute>
          <AppLayout><Lazy><AIInsightsTelemetryPage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/perf" element={
        <ProtectedRoute adminOnly>
          <AppLayout><Lazy><PerformancePage /></Lazy></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary level="app">
      <Router>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
